import { CareEnvironment, CarePhase } from '../../../../appModel';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH, unlimitedMaxBenefitMagicNumber } from '../sharedConstants';
import {
  computeMonthlyCostMetrics,
  computeNon2AdlDaysThisRow,
} from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironment,
  compoundFactor,
  getCalendarPolicyYear,
  getCareCostAnchorYear,
  getDailyBenefitAmountForEnvironment,
  getFirstContributingPhaseStartYear,
  getPhaseCareMonthsByPhase,
  getMaxMonthlyBenefitAmount,
  getMonthlyBenefitAmountForEnvironment,
} from '../sharedPolicyConfig';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import {
  deriveIneligibilityReason,
  deriveIneligibleDayBreakdown,
} from '../deriveIneligibilityReason';
import { buildPrePolicyMonth } from '../buildPrePolicyMonth';

// ---------------------------------------------------------------------------
// Traditional / standalone LTC insurance — generic reimbursement product.
// Also models CareScout Care Assurance, which runs the identical generic
// standalone-LTC calc path (one strategy serves both).
//
// SINGLE POOL, NO DEATH BENEFIT:
// - `policyMaximumBenefitAmount` IS the lifetime LTC dollar pool (the
//   "Coverage Maximum" on CareScout, p.2). Unlike the asset-based hybrids,
//   this is NOT a death benefit — traditional LTC has no death benefit at
//   all. We seed `baseBenefitPool` from it (less utilized-to-date) and keep
//   `extensionOfBenefitsPool = 0` (no second pool / continuation rider) and
//   `deathBenefit = 0` (no DB pool). `finalizeDeathBenefit` returns 0 and
//   every month emits `deltas.deathBenefit = { reduction: 0, flooredAt-
//   Minimum: false }`.
//
// PAYOUT MODEL — reimbursement (default) or cash indemnity:
// - Reimbursement: pay `min(eligibleCost, proRatedDailyCap)` from the pool,
//   where `proRatedDailyCap = monthlyCap × (eligibleDays/30)` and
//   `monthlyCap = monthlyBenefitForEnvironment × compoundFactor(rate, ...)`.
//   Excess care cost above the cap is out-of-pocket.
// - Indemnity (gated on `isIndemnityPolicyPayment === true`): pay
//   `min(proRatedCap, poolRemaining)` as cash regardless of actual cost,
//   drawn from the single pool. Matches the legacy indemnity tail; default
//   is reimbursement.
//
// DAILY-CAP INFLATION (at-issue-anchored, compound):
// - `dailyBenefit × compoundFactor(rate, capYears, policyYear)`. The per-
//   month dollar cap steps up on each policy anniversary, anchored to the
//   at-issue daily benefit — same mechanic the hybrids use for their cap.
//
// POOL INFLATION (REMAINING-BALANCE, the carrier-accurate fix):
// - CareScout p.2 states inflation "automatically increases your Daily
//   Benefit Amount and the Coverage Maximum *remaining* after any claim
//   payments." So the pool compounds on the REMAINING balance, not the at-
//   issue amount: at each policy anniversary (gated on `pool > 0` and within
//   `capYears`) we accrue `inflationAccrual = openPool × rate`. This differs
//   from the hybrids' at-issue-anchored accrual on purpose — it is carrier-
//   accurate for traditional LTC. The universal identity
//   `close = open + inflationAccrual − depletion` still holds.
// - For SIMPLE inflation (`isSimpleInflationProtection`), we accrue a
//   constant increment `atIssuePool × rate` each anniversary instead of
//   compounding the remaining balance — simple inflation grows the benefit
//   by a fixed dollar step per year, not as a percentage of the current
//   (drawn-down) balance.
//
// PRE-CLAIM POOL INFLATION:
// - Anniversaries between policy issue and claim start are not iterated (the
//   projection only spans claim months), so the undrawn pool has already
//   compounded N−1 anniversaries when a claim starts in policy year N. We
//   pre-inflate the opening pool in `initClaimState` via
//   `compoundFactor(rate, capYears, claimStartPolicyYear)` (compound) so the
//   opening balance matches the carrier's current pool. (Simple inflation's
//   pre-claim growth is also folded in via a linear factor — see below.)
//
// 2-ADL GATE + WAITING PERIOD (elimination / deductible period):
// - Standard `computeNon2AdlDaysThisRow` → `consumeWaitingPeriodDaysFor-
//   Environment`. EP days are strictly out-of-pocket; there is NO
//   retroactive lump (CareScout / generic trad-LTC default has none), so we
//   pass `retroactiveLumpPaid: 0` to the milestone deriver.
//
// `policyBenefitPeriodMonths` is NOT a cap here — the dollar pool is the
// only cap. Like Asset Care, neither a per-claim nor a per-month carry-
// forward is needed beyond the universal slots, so both state and deltas
// shapes are empty.
// ---------------------------------------------------------------------------

export type TraditionalLtcState = Record<string, never>;
export type TraditionalLtcDeltas = Record<string, never>;

interface InflationConfig {
  rate: number;
  capYears: number;
  isSimple: boolean;
}

function readInflation(ltcPolicy: PolicyFundingSource): InflationConfig {
  const capYearsRaw = ltcPolicy.policyInflationProtectionYears;
  return {
    rate: ltcPolicy.policyInflationProtection ?? 0,
    // `compoundFactor` treats capYears <= 0 as uncapped; null/0 here means
    // lifetime/uncapped inflation, which maps cleanly onto that contract.
    capYears: capYearsRaw && capYearsRaw > 0 ? capYearsRaw : 0,
    isSimple: ltcPolicy.isSimpleInflationProtection === true,
  };
}

// A standalone LTC policy with a null/undefined `policyMaximumBenefitAmount`
// is UNLIMITED, not $0 — matching legacy `isUnlimitedBenefit` (a missing max
// on a non-hybrid policy returns `unlimitedMaxBenefitMagicNumber` and flags
// `policyBenefitIsUnlimited`). A populated max (including 0) is a real cap.
function isUnlimitedPool(ltcPolicy: PolicyFundingSource): boolean {
  return ltcPolicy.policyMaximumBenefitAmount == null;
}

// At-issue lifetime dollar pool. Unlimited policies seed the sentinel so
// payouts are never pool-limited; the client renders any pool >= the sentinel
// as "Unlimited". A populated max (including 0) is used verbatim.
function getAtIssuePool(ltcPolicy: PolicyFundingSource): number {
  if (isUnlimitedPool(ltcPolicy)) return unlimitedMaxBenefitMagicNumber;
  return ltcPolicy.policyMaximumBenefitAmount ?? 0;
}

// Pool-growth factor at a given policy year. Compound inflation defers to
// `compoundFactor`. Simple inflation grows the pool by a fixed dollar step
// of `atIssue × rate` each anniversary, so the factor is the linear
// `1 + rate × (effectiveYear − 1)` (capped at `capYears`).
function poolGrowthFactor(
  { rate, capYears, isSimple }: InflationConfig,
  policyYear: number,
): number {
  if (!isSimple) return compoundFactor(rate, capYears, policyYear);
  const effectiveYear = capYears > 0 && policyYear > capYears ? capYears : policyYear;
  const steps = Math.max(effectiveYear - 1, 0);
  return 1 + rate * steps;
}

export const traditionalLtcStrategy: PolicyProjectionStrategy<
  TraditionalLtcState,
  TraditionalLtcDeltas
> = {
  initClaimState(client, ltcPolicy): MonthlyState<TraditionalLtcState> {
    const atIssuePool = getAtIssuePool(ltcPolicy);
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const inflation = readInflation(ltcPolicy);
    // Anniversaries between policy issue and claim start are not iterated
    // (the projection only spans claim months), so pre-inflate the pool to
    // its value at claim start. Claims starting in policy year 1 keep the
    // factor at 1.0. Utilized-to-date is subtracted after inflating, so it
    // reduces the already-grown pool.
    // Unlimited pools stay flat at the sentinel — no pre-claim inflation, no
    // utilized-to-date reduction (legacy's unlimited path returns the magic
    // number verbatim, before any of that logic runs).
    const claimStartPolicyYear = getCalendarPolicyYear(client as Client, 0);
    const poolAtClaimStart = isUnlimitedPool(ltcPolicy)
      ? atIssuePool
      : atIssuePool * poolGrowthFactor(inflation, claimStartPolicyYear);
    return {
      pools: {
        baseBenefitPool: isUnlimitedPool(ltcPolicy)
          ? atIssuePool
          : Math.max(poolAtClaimStart - utilizedToDate, 0),
        // Traditional LTC has no continuation-of-benefits pool and no death
        // benefit — both stay at 0 for the life of the projection.
        extensionOfBenefitsPool: 0,
        deathBenefit: 0,
      },
      waitingPeriod: {
        homeDays:
          ltcPolicy.homeCareWaitingPeriodDaysRemaining ??
          ltcPolicy.homeCareWaitingPeriodDays ??
          0,
        facilityDays:
          ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
          ltcPolicy.facilityCareWaitingPeriodDays ??
          0,
      },
      strategyState: {},
    };
  },

  buildClaimTerms(client, ltcPolicy): ClaimTerms {
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const inflation = readInflation(ltcPolicy);
    const clientAsClient = client as Client;
    const projectionAnchorYear = getCareCostAnchorYear(clientAsClient);
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    return {
      strategyId: 'traditional-ltc',
      nominalDailyBenefit: dailyBenefit,
      nominalDailyBenefitBase: dailyBenefit,
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: ltcPolicy.dailyHomeBenefitAmount ?? 0,
        [CareEnvironment.assistedLiving]: ltcPolicy.dailyAssistedLivingBenefitAmount ?? 0,
        [CareEnvironment.fullCareFacility]: ltcPolicy.dailyNursingHomeBenefitAmount ?? 0,
        [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.independentLiving,
        ),
      },
      // Single product-wide inflation rate drives both the daily-cap step and
      // the pool growth — populate the policy-wide and base-pool knobs from
      // it. There is no separate face/COB rate on traditional LTC, so leave
      // the EOB-pool knobs undefined (no second pool).
      inflationRate: inflation.rate,
      inflationCapYears: inflation.capYears,
      inflationIsSimple: inflation.isSimple,
      baseBenefitPoolInflationRate: inflation.rate,
      baseBenefitPoolInflationCapYears: inflation.capYears,
      baseBenefitPoolAtIssue: getAtIssuePool(ltcPolicy),
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
      firstPhaseStartYear: getFirstContributingPhaseStartYear(clientAsClient),
      policyPremiumStartYear: ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear,
      // No death benefit on traditional LTC.
      minimumGuaranteedDeathBenefit: 0,
      isReimbursement: !isIndemnity,
      isIndemnity,
      isUnlimited: isUnlimitedPool(ltcPolicy),
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear, calendarYearOffset } = input;
    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const inflation = readInflation(ltcPolicy);
    const factor = poolGrowthFactor(inflation, policyYear);

    const atIssuePool = getAtIssuePool(ltcPolicy);
    const inflatedMonthlyCap = baseMonthly * factor;
    // The snapshot's effective total LTC benefit reflects the inflated pool
    // ceiling assuming no draws this year (illustration "no claims paid"
    // framing). Actual remaining pool is carried on the month state. Unlimited
    // pools stay flat at the sentinel (the client renders >= sentinel as
    // "Unlimited"); only the daily/monthly cap inflates.
    const inflatedPool = isUnlimitedPool(ltcPolicy) ? atIssuePool : atIssuePool * factor;

    return {
      policyYear,
      calendarYearOffset,
      effectiveDailyBenefit: inflatedMonthlyCap / DAYS_IN_MONTH,
      effectiveDailyBenefitBase: inflatedMonthlyCap / DAYS_IN_MONTH,
      effectiveDailyBenefitRider: 0,
      // No death benefit / continuation pool on traditional LTC.
      effectiveFaceAmount: 0,
      effectiveContinuationBenefit: 0,
      effectiveTotalLtcBenefit: inflatedPool,
      monthlyBenefitCap: inflatedMonthlyCap,
      theoreticalLtcPoolCeiling: inflatedPool,
    };
  },

  computeMonth(
    input: MonthComputeInput<TraditionalLtcState, TraditionalLtcDeltas>,
  ): MonthlyEntry<TraditionalLtcState, TraditionalLtcDeltas> {
    const { client, ltcPolicy, phaseCarePlan, monthlyCareInput, openState, priorMonth } = input;
    const { monthIndex, carePhase, monthsIntoPhase } = monthlyCareInput;
    const clientAsClient = client as Client;

    // Cost metrics + structural policy year, resolved up front so the
    // pre-policy gate runs before any WP/ADL classification.
    const costMetrics = computeMonthlyCostMetrics(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );
    const calendarYearOffset = Math.floor(monthIndex / 12);
    const policyYear = getCalendarPolicyYear(clientAsClient, monthIndex);

    const context: MonthContext = {
      monthIndex,
      policyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYear),
      calendarYearOffset,
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
    };

    // Pre-policy gate (see buildPrePolicyMonth). Single-life traditional LTC
    // floors its policy year at 1 via getCalendarPolicyYear, so this is
    // defensive — a care month before the premium-start year pays $0, full OOP.
    if (policyYear < 1) {
      const prePolicyFacts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        appliedMonthlyBenefitCap: 0,
        appliedCapPoolSource: 'base',
      };
      return buildPrePolicyMonth<TraditionalLtcState, TraditionalLtcDeltas>({
        context,
        facts: prePolicyFacts,
        openState,
        strategyDeltas: {},
      });
    }

    // 1. Day classification (2-ADL bucket → WP → eligible).
    const non2AdlDays = computeNon2AdlDaysThisRow(
      clientAsClient,
      phaseCarePlan,
      monthsIntoPhase,
    );
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDays;

    // Deductible / elimination period. CareScout's 90-day deductible applies
    // regardless of care setting (both home and facility WP buckets seeded to
    // the same value); the shared two-tier WP machinery handles per-setting
    // routing and the 1st-Day Home Care waiver (home bucket = 0) when elected.
    const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
      openState.waitingPeriod,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      closeRelevantWp,
    } = wpConsumption;
    const eligibleDays = daysAfter2Adl - wpDaysThisRow;

    // 2. Per-month dollar cap (this year's inflation factor). `costMetrics` and
    //    `policyYear` were resolved up front for the pre-policy gate.
    const inflation = readInflation(ltcPolicy);

    const baseMonthly = getMonthlyBenefitAmountForEnvironment(
      ltcPolicy,
      phaseCarePlan.appliedCareEnvironment,
    );
    // Daily cap inflates at-issue-anchored (compound for compound inflation;
    // linear step for simple), consistent with the pool's growth model.
    const monthlyCap = baseMonthly * poolGrowthFactor(inflation, policyYear);

    // 3. Annual anniversary pool accrual on the REMAINING balance (carrier-
    //    accurate per CareScout p.2). Gated on "pool still > 0" so an
    //    exhausted pool stays exhausted. Compound inflation grows the
    //    remaining balance by `rate`; simple inflation adds a constant
    //    `atIssue × rate` step (independent of draws). Within capYears only.
    let inflationAccrual = 0;
    if (priorMonth && policyYear !== priorMonth.context.policyYear) {
      const withinCap = inflation.capYears <= 0 || policyYear <= inflation.capYears;
      // Unlimited pools stay flat at the sentinel — accruing inflation on it
      // would drift the pool ceiling and break parity with legacy's flat magic
      // number (the daily/monthly cap still inflates via `poolGrowthFactor`).
      if (
        !isUnlimitedPool(ltcPolicy) &&
        openState.pools.baseBenefitPool > 0 &&
        withinCap &&
        inflation.rate > 0
      ) {
        inflationAccrual = inflation.isSimple
          ? getAtIssuePool(ltcPolicy) * inflation.rate
          : openState.pools.baseBenefitPool * inflation.rate;
      }
    }
    const poolAfterAccrual = openState.pools.baseBenefitPool + inflationAccrual;

    // 4. Payout: reimbursement (default) or cash indemnity. Single pool only.
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    const proRatedCap = monthlyCap * (eligibleDays / DAYS_IN_MONTH);
    const eligibleCost = costMetrics.dailyCareCost * eligibleDays;

    let drawFromPool = 0;
    let indemnityPaid = 0;
    if (isIndemnity && poolAfterAccrual > 0 && eligibleDays > 0) {
      // Pay capped cash regardless of actual cost; cannot exceed the pool.
      const indemnityAmount = Math.min(proRatedCap, poolAfterAccrual);
      drawFromPool = indemnityAmount;
      indemnityPaid = indemnityAmount;
    } else {
      // Reimbursement: pay min(eligibleCost, proRatedCap), drawn from pool.
      const wouldPay = Math.min(eligibleCost, proRatedCap);
      drawFromPool = Math.min(wouldPay, poolAfterAccrual);
    }
    const ltcCoveragePaid = drawFromPool;

    // 5. Closing balance. No death benefit to deplete.
    const closePool = poolAfterAccrual - drawFromPool;

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      careCostInflationYears: costMetrics.careCostInflationYears,
      projectedCareYear: costMetrics.projectedCareYear,
      non2AdlDays,
      coverageEligibleDays: eligibleDays,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      appliedMonthlyBenefitCap: monthlyCap,
      appliedCapPoolSource: 'base',
    };

    const payout: PayoutSummary = {
      ltcCoveragePaid,
      ltcPayoutToCost: ltcCoveragePaid,
      ineligibleCost: costMetrics.effectiveCareCost - ltcCoveragePaid,
      indemnityPayout: indemnityPaid,
    };

    const deltas: MonthlyDeltas<TraditionalLtcDeltas> = {
      payout,
      base: { depletion: drawFromPool, inflationAccrual },
      // No second pool — EOB flow is always zero.
      eob: { depletion: 0, inflationAccrual: 0 },
      // No death benefit on traditional LTC.
      deathBenefit: { reduction: 0, flooredAtMinimum: false },
      strategyDeltas: {},
    };

    const closeState: MonthlyState<TraditionalLtcState> = {
      pools: {
        baseBenefitPool: closePool,
        extensionOfBenefitsPool: 0,
        deathBenefit: 0,
      },
      waitingPeriod: { homeDays: closeHomeWp, facilityDays: closeFacilityWp },
      strategyState: {},
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closePool <= 0,
      clientPoolExhausted: closePool <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    const milestones = deriveStandardMilestones<TraditionalLtcState, TraditionalLtcDeltas>({
      context,
      facts,
      openState,
      closeState,
      deltas,
      priorMonth,
      retroactiveLumpPaid: 0,
      minimumGuaranteedDeathBenefit: 0,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      hasInflationProtection: inflation.rate > 0,
    });

    const ineligibilityInput = {
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState,
      closeState,
      payout,
    };
    const ineligibilityReason =
      deriveIneligibilityReason<TraditionalLtcState>(ineligibilityInput);
    const ineligibleDayBreakdown =
      deriveIneligibleDayBreakdown<TraditionalLtcState>(ineligibilityInput);

    return {
      context,
      facts,
      openState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason, ineligibleDayBreakdown },
    };
  },

  // Traditional LTC has no death benefit — always 0.
  finalizeDeathBenefit(_input: FinalizeDeathBenefitInput<TraditionalLtcState>): number {
    return 0;
  },
};
